/**
 * ProfessionalMenuPage Component
 * Admin menu for salon management features
 * 
 * Features:
 * - Service management (Categories, Catalog)
 * - Team management (Professionals)
 * - Station management (Types, Stations)
 * - Financial (Commissions)
 * - Settings (Company Profile, Users)
 * - User Profile
 */

import { useAuthStore } from '../stores/authStore';
import { useViewModeStore } from '../stores/viewModeStore';
import { useNavigation } from '../shared/hooks/useNavigation';
import { ROUTES } from '../shared/navigation';
import { 
  User,
  Scissors,
  FolderOpen,
  Palette,
  Users,
  MapPin,
  Settings,
  DollarSign,
  Building2,
  UserCog,
  ChevronRight,
  ArrowLeft,
  ToggleLeft,
  ToggleRight,
  LogOut,
  Paintbrush
} from 'lucide-react';

import ProfessionalBottomNavigation from '../components/ProfessionalBottomNavigation';

const ProfessionalMenuPage = () => {
  const { navigate } = useNavigation();
  const { logout, user: _user } = useAuthStore();
  const { currentMode, toggleMode } = useViewModeStore();

  // Menu sections with their options
  const menuSections = [
    {
      id: 'services',
      title: 'Serviços',
      icon: Scissors,
      color: 'bg-purple-50 border-purple-200 text-purple-700',
      items: [
        {
          label: 'Categorias',
          path: '/coming-soon',
          icon: FolderOpen
        },
        {
          label: 'Catálogo de Serviços',
          path: '/coming-soon',
          icon: Palette
        }
      ]
    },
    {
      id: 'professionals',
      title: 'Profissionais',
      icon: Users,
      color: 'bg-green-50 border-green-200 text-green-700',
      items: [
        {
          label: 'Equipe',
          path: '/coming-soon',
          icon: Users
        }
      ]
    },
    {
      id: 'stations',
      title: 'Estações',
      icon: MapPin,
      color: 'bg-orange-50 border-orange-200 text-orange-700',
      items: [
        {
          label: 'Tipo de Estação',
          path: '/coming-soon',
          icon: Settings
        },
        {
          label: 'Estações',
          path: '/coming-soon',
          icon: MapPin
        }
      ]
    },
    {
      id: 'financial',
      title: 'Financeiro',
      icon: DollarSign,
      color: 'bg-yellow-50 border-yellow-200 text-yellow-700',
      items: [
        {
          label: 'Comissões',
          path: '/coming-soon',
          icon: DollarSign
        }
      ]
    },
    {
      id: 'settings',
      title: 'Configurações',
      icon: Settings,
      color: 'bg-gray-50 border-gray-200 text-gray-700',
      items: [
        {
          label: 'Personalizar Tema',
          path: '/professional/theme',
          icon: Paintbrush
        },
        {
          label: 'Perfil da Empresa',
          path: '/coming-soon',
          icon: Building2
        },
        {
          label: 'Usuários',
          path: '/coming-soon',
          icon: UserCog
        }
      ]
    },
    {
      id: 'profile',
      title: 'Perfil',
      icon: User,
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      items: [
        {
          label: 'Meu Perfil',
          path: '/profile',
          icon: User
        }
      ]
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleBack = () => {
    navigate(ROUTES.PROFESSIONAL.DASHBOARD);
  };

  const handleModeToggle = () => {
    if (currentMode === 'professional') {
      toggleMode(); // This switches to client mode
      navigate(ROUTES.DASHBOARD); // Go to client dashboard
    } else {
      toggleMode(); // This switches to professional mode  
      navigate(ROUTES.PROFESSIONAL.DASHBOARD); // Go to professional dashboard
    }
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  return (
    <div className="h-screen flex flex-col bg-brand-light dark:bg-brand-dark">
      {/* Header - Luxury Design */}
      <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 px-4 py-4 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 border border-brand-dark/20 dark:border-brand-light/20 transition-all duration-300"
            aria-label="Voltar"
          >
            <ArrowLeft size={24} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
          </button>
          <h1 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Menu Administrativo</h1>
        </div>
      </div>

      {/* Menu Content - Scrollable - Luxury Design */}
      <div className="flex-1 overflow-y-auto px-4 py-6 mobile-scroll">
        <div className="space-y-6">
          {menuSections.map((section) => {
            const SectionIcon = section.icon;

            return (
              <div key={section.id} className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20">
                {/* Section Header - Monochromatic */}
                <div className="flex items-center space-x-3 p-4 border-b border-brand-dark/10 dark:border-brand-light/10 bg-brand-dark/5 dark:bg-brand-light/5">
                  <SectionIcon size={24} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
                  <h2 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">{section.title}</h2>
                </div>

                {/* Section Items */}
                <div className="divide-y divide-brand-dark/10 dark:divide-brand-light/10">
                  {section.items.map((item, index) => {
                    const ItemIcon = item.icon;

                    return (
                      <button
                        key={index}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center justify-between p-4 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
                      >
                        <div className="flex items-center space-x-3">
                          <ItemIcon size={20} className="text-brand-dark/60 dark:text-brand-light/60" strokeWidth={1.5} />
                          <span className="text-brand-dark dark:text-brand-light font-medium tracking-wide">{item.label}</span>
                        </div>
                        <ChevronRight size={20} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mode Switch and Logout Section - Luxury Design */}
        <div className="mt-6 space-y-4">
          {/* Mode Switch */}
          <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20">
            <button
              onClick={handleModeToggle}
              className="w-full flex items-center justify-between p-4 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
            >
              <div className="flex items-center space-x-3">
                {currentMode === 'professional' ? (
                  <ToggleRight size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
                ) : (
                  <ToggleLeft size={20} className="text-brand-dark/60 dark:text-brand-light/60" strokeWidth={1.5} />
                )}
                <div className="text-left">
                  <span className="text-brand-dark dark:text-brand-light font-medium block tracking-wide">
                    {currentMode === 'professional' ? 'Modo Profissional' : 'Modo Cliente'}
                  </span>
                  <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                    Toque para alternar para {currentMode === 'professional' ? 'modo cliente' : 'modo profissional'}
                  </span>
                </div>
              </div>
              <ChevronRight size={20} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
            </button>
          </div>

          {/* Logout */}
          <div className="bg-brand-light dark:bg-brand-dark border border-red-600/30 dark:border-red-400/30">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-between p-4 hover:bg-red-600/5 dark:hover:bg-red-400/5 transition-all duration-300"
            >
              <div className="flex items-center space-x-3">
                <LogOut size={20} className="text-red-600 dark:text-red-400" strokeWidth={1.5} />
                <div className="text-left">
                  <span className="text-brand-dark dark:text-brand-light font-medium block tracking-wide">Sair</span>
                  <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Desconectar da conta</span>
                </div>
              </div>
              <ChevronRight size={20} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
            </button>
          </div>
          {/* Add bottom padding for navigation */}
          <div className="h-20"></div>
        </div>
      </div>

      {/* Bottom Navigation - Fixed */}
      <div className="flex-shrink-0">
        <ProfessionalBottomNavigation />
      </div>

    </div>
  );
};

export default ProfessionalMenuPage;